import { useRef, useState } from "react";
import { api, apiError } from "@/lib/apiClient";
import { useWorkspace } from "@/context/WorkspaceContext";
import { PageHeader, EmptyState } from "@/components/PageBits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Plus, Trash2, FolderKanban } from "lucide-react";
import { toast } from "sonner";

export default function Projects() {
  const { orgId, projects, loadProjects, selectProject, currentOrg, loading, error } = useWorkspace();
  const activeOrg = useRef(orgId);
  activeOrg.current = orgId;
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);

  const create = async () => {
    if (loading || !orgId || !currentOrg) {
      toast.error("Select an organization before creating a project.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/orgs/${orgId}/projects`, { name, description });
      toast.success("Project created");
      setOpen(false); setName(""); setDescription("");
      if (activeOrg.current === orgId) {
        await loadProjects(orgId);
        if (activeOrg.current === orgId) selectProject(data.id);
      }
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/projects/${id}`);
      toast.success("Project deleted");
      await loadProjects(orgId);
    } catch (e) { toast.error(apiError(e)); }
  };

  const createBtn = (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="new-project-button" disabled={loading || !currentOrg} className="bg-zinc-50 text-zinc-900 hover:bg-zinc-200">
          <Plus className="mr-2 h-4 w-4" /> New project
        </Button>
      </DialogTrigger>
      <DialogContent className="border-zinc-800 bg-zinc-950" data-testid="new-project-dialog">
        <DialogHeader><DialogTitle className="text-zinc-100">Create project</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label className="text-zinc-300">Name</Label>
            <Input data-testid="project-name-input" value={name} onChange={(e) => setName(e.target.value)}
              className="bg-zinc-950 border-zinc-800" placeholder="my-app-prod" />
          </div>
          <div className="space-y-2">
            <Label className="text-zinc-300">Description</Label>
            <Textarea data-testid="project-description-input" value={description} onChange={(e) => setDescription(e.target.value)}
              className="bg-zinc-950 border-zinc-800" placeholder="Optional" />
          </div>
        </div>
        <DialogFooter>
          <Button data-testid="create-project-confirm" disabled={!name.trim() || busy || loading || !currentOrg} onClick={create}
            className="bg-zinc-50 text-zinc-900 hover:bg-zinc-200">{busy ? "Creating…" : "Create"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  return (
    <div data-testid="projects-page">
      <PageHeader title="Projects" description={`Isolated workspaces in ${currentOrg?.name || "your org"}.`} actions={createBtn} />
      {loading ? <p role="status">Loading workspace…</p> : error ? (
        <p role="alert">Unable to load workspace. Please reload the page.</p>
      ) : !currentOrg ? <p role="status">No organization available for this account.</p> : null}
      {projects.length === 0 ? (
        <EmptyState testId="projects-empty" title="No projects yet"
          description="A project isolates rooms, API keys and usage. Create one to begin." action={createBtn} />
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {projects.map((p) => (
            <div key={p.id} className="group rounded-md border border-zinc-800 bg-zinc-900/30 p-5" data-testid={`project-card-${p.name}`}>
              <div className="flex items-start justify-between">
                <div className="flex h-9 w-9 items-center justify-center rounded-md border border-zinc-800 bg-zinc-950">
                  <FolderKanban className="h-4 w-4 text-zinc-400" />
                </div>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <button data-testid={`delete-project-${p.name}`} className="text-zinc-600 transition-colors hover:text-red-400">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </AlertDialogTrigger>
                  <AlertDialogContent className="border-zinc-800 bg-zinc-950">
                    <AlertDialogHeader>
                      <AlertDialogTitle className="text-zinc-100">Delete “{p.name}”?</AlertDialogTitle>
                      <AlertDialogDescription className="text-zinc-400">
                        This removes all rooms and API keys in this project. This cannot be undone.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel className="border-zinc-800 bg-zinc-900">Cancel</AlertDialogCancel>
                      <AlertDialogAction data-testid={`confirm-delete-project-${p.name}`} onClick={() => remove(p.id)}
                        className="bg-red-600 text-white hover:bg-red-500">Delete</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
              <h3 className="mt-4 font-medium text-zinc-100">{p.name}</h3>
              <p className="mt-1 line-clamp-2 text-sm text-zinc-500">{p.description || "No description"}</p>
              <div className="mt-4 flex gap-4 font-mono text-xs text-zinc-500">
                <span>{p.room_count} rooms</span>
                <span>{p.api_key_count} keys</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
